import React, { useEffect, useMemo, useState } from 'react';
import type { RegisterSession, CurrentUser, StaffMember } from '../../types';

/**
 * One-time toast that fires the first time this device renders POS or KDS
 * against a register session opened by a different user. Tells floor staff
 * "you're sharing the cashier's register, sales here flow to that session"
 * so they understand why no Open Register button appears.
 *
 * Dismiss writes a sessionStorage marker keyed by sessionId, so the banner
 * never reappears for the same session on this device. A new session (after
 * the register is closed and re-opened) will show it again the first time
 * this device sees the new session.
 */

interface SharedRegisterBannerProps {
  session: RegisterSession | null | undefined;
  currentUser: CurrentUser | StaffMember | null | undefined;
}

const SEEN_PREFIX = 'shared_register_banner_seen:';

function formatTime(ms?: number): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

const SharedRegisterBanner: React.FC<SharedRegisterBannerProps> = ({ session, currentUser }) => {
  // Track sessionId on a re-mountable signal so dismissals don't bleed across
  // sessions (close + reopen => different id => banner shows again).
  const [dismissedSessionId, setDismissedSessionId] = useState<string | null>(null);

  // On every session change, hydrate the dismissed flag from sessionStorage so
  // a hard refresh doesn't re-display the banner for an already-seen session.
  useEffect(() => {
    if (!session?.id) {
      setDismissedSessionId(null);
      return;
    }
    try {
      const seen = sessionStorage.getItem(`${SEEN_PREFIX}${session.id}`);
      if (seen) {
        setDismissedSessionId(String(session.id));
      } else {
        setDismissedSessionId(null);
      }
    } catch {
      /* private mode / quota — best-effort, just don't persist */
    }
  }, [session?.id]);

  const isAdopting = useMemo(() => {
    if (!session || !session.isActive || !currentUser) return false;
    return String((currentUser as any).id) !== String(session.cashierId);
  }, [session, currentUser]);

  if (!isAdopting || !session) return null;
  if (dismissedSessionId === String(session.id)) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(`${SEEN_PREFIX}${session.id}`, '1');
    } catch {
      /* ignore */
    }
    setDismissedSessionId(String(session.id));
  };

  const time = formatTime(session.startTime);
  const cashier = session.cashierName ?? 'another user';
  const terminal = session.terminalName || session.terminalId || 'another terminal';

  return (
    <div className="px-4 pt-3">
      <div
        role="status"
        aria-live="polite"
        className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300"
      >
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 9v2" />
            <path d="M12 17h.01" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">You're sharing the register</p>
          <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
            Opened by <span className="font-semibold">{cashier}</span> on{' '}
            <span className="font-semibold">{terminal}</span>
            {time ? <> at <span className="font-semibold">{time}</span></> : null}.
            Sales rung up here will flow to that session.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 inline-flex items-center justify-center rounded-md bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-semibold px-3 py-1.5 transition focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default SharedRegisterBanner;
